import Dexie from 'dexie';

// Create Dexie database
export const db = new Dexie('weddingPlatformDB');

// Define database schema
db.version(2).stores({
  users: '++id, email, &username',
  events: '++id, userId, name, date, *tags',
  invitations: '++id, eventId, name, published',
  guests: '++id, eventId, name, email, rsvpStatus, invitationId',
  templates: '++id, userId, name', // Keep this indexed, but elements will be handled separately
}).upgrade(tx => {
  return tx.templates.toCollection().modify(template => {
    // Ensure template.elements is a valid array
    if (!template.elements) template.elements = [];
    // Any other data migrations needed
  });
});

// User functions
export const userService = {
  async login(email, password) {
    try {
      // Try to find the user by email
      const user = await db.users.where('email').equals(email).first();
      
      if (!user) {
        throw new Error('User not found');
      }
      
      // Simple password validation (in a real app, you'd use proper hashing)
      if (user.password !== password) {
        throw new Error('Invalid password');
      }
      
      // Return user without password
      const { password: _, ...userWithoutPassword } = user;
      return userWithoutPassword;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },
  
  async register(userData) {
    try {
      // Check if user already exists
      const existing = await db.users.where({ username: userData.username }).first();
      if (existing) {
        throw new Error('Username already exists');
      }
      
      // Add user to database
      const id = await db.users.add({
        ...userData,
        created: new Date().toISOString()
      });
      
      // Return user without password
      const { password, ...userWithoutPassword } = userData;
      return { id, ...userWithoutPassword };
    } catch (error) {
      throw error;
    }
  },
  
  async getById(id) {
    const user = await db.users.get(id);
    if (!user) return null;
    
    const { password, ...userWithoutPassword } = user;
    return userWithoutPassword;
  }
};

// Event functions
export const eventService = {
  async getAll(userId) {
    return await db.events.where({ userId }).toArray();
  },
  
  async getById(id) {
    console.log("getById called with:", id, typeof id);
    
    // If ID is not a number, try to parse it
    const eventId = typeof id === 'number' ? id : parseInt(id, 10);
    
    if (isNaN(eventId)) {
      console.error("Invalid event ID:", id);
      throw new Error("Invalid event ID");
    }
    
    try {
      // First try direct lookup by ID
      const event = await db.events.get(eventId);
      
      if (event) {
        console.log("Found event by direct lookup:", event);
        return event;
      }
      
      // If that fails, do a search by ID field
      const events = await db.events.where('id').equals(eventId).toArray();
      
      if (events && events.length > 0) {
        console.log("Found event by query:", events[0]);
        return events[0];
      }
      
      console.log("No event found with ID:", eventId);
      return null;
    } catch (error) {
      console.error("Error in getById:", error);
      throw error;
    }
  },
  
  async create(eventData) {
    return await db.events.add({
      ...eventData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  },
  
  async update(id, eventData) {
    await db.events.update(id, {
      ...eventData,
      updatedAt: new Date().toISOString()
    });
    return await db.events.get(id);
  },
  
  async delete(id) {
    // Delete associated data
    await db.invitations.where({ eventId: id }).delete();
    await db.guests.where({ eventId: id }).delete();
    
    // Delete the event itself
    return await db.events.delete(id);
  },

  // Add helper methods to get invitations and guests
  async getInvitations(eventId) {
    console.log("Getting invitations for event:", eventId);
    return await db.invitations.where('eventId').equals(eventId).toArray();
  },
  
  async getGuests(eventId) {
    console.log("Getting guests for event:", eventId);
    return await db.guests.where('eventId').equals(eventId).toArray();
  },

  async getAllEvents() {
    return await db.events.toArray();
  },

  createSlug(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  },

  async getBySlug(slug) {
    const events = await db.events.toArray();
    return events.find(event => this.createSlug(event.name) === slug);
  }
};

// Invitation functions
export const invitationService = {
  async getByEventId(eventId) {
    console.log("Getting invitations for event:", eventId);
    return await db.invitations
      .where('eventId')
      .equals(parseInt(eventId))
      .toArray();
  },
  
  async getById(id) {
    console.log("Getting invitation with ID:", id);
    // Try direct lookup first
    const invitation = await db.invitations.get(id);
    if (invitation) return invitation;
    
    // If no results, try parsing as number if it's a string
    if (typeof id === 'string') {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId)) {
        return await db.invitations.get(numericId);
      }
    }
    return null;
  },
  
  async create(invitationData) {
    console.log("Creating invitation:", invitationData);
    const id = await db.invitations.add({
      ...invitationData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return await db.invitations.get(id);
  },
  
  async update(id, invitationData) {
    console.log("==== INVITATION UPDATE DEBUG ====");
    console.log("Updating invitation with ID:", id, "Type:", typeof id);
    console.log("Invitation data keys:", Object.keys(invitationData));
    
    try {
      // Make sure id is valid - handle both string and number cases
      let parsedId = id;
      if (typeof id === 'string' && !isNaN(parseInt(id))) {
        parsedId = parseInt(id);
        console.log("Converted string ID to number:", parsedId);
      }
      
      // Create a safe copy of the data without the ID field
      const { id: _, ...dataToUpdate } = invitationData;
      
      // Ensure elements is an array
      if (!dataToUpdate.elements) {
        dataToUpdate.elements = [];
      }
      
      // Add updated timestamp
      dataToUpdate.updatedAt = new Date().toISOString();
      
      console.log("Performing update on id:", parsedId);
      console.log("Update data prepared:", {
        ...dataToUpdate,
        elementsCount: dataToUpdate.elements.length
      });
      
      // Actually perform the update
      const updateCount = await db.invitations.update(parsedId, dataToUpdate);
      console.log("Update operation result count:", updateCount);
      
      if (updateCount === 0) {
        console.error("No records were updated!");
        throw new Error("Invitation not found or no changes made");
      }
      
      // Retrieve the updated invitation
      const updatedInvitation = await db.invitations.get(parsedId);
      console.log("Retrieved updated invitation:", updatedInvitation ? "SUCCESS" : "FAILED");
      
      if (!updatedInvitation) {
        throw new Error("Failed to retrieve updated invitation");
      }
      
      return updatedInvitation;
    } catch (error) {
      console.error("Error in invitationService.update:", error);
      throw error;
    } finally {
      console.log("==== END INVITATION UPDATE DEBUG ====");
    }
  },
  
  async delete(id) {
    return await db.invitations.delete(id);
  }
};

// Guest functions
export const guestService = {
  async getByEventId(eventId) {
    return await db.guests.where({ eventId }).toArray();
  },
  
  async getById(id) {
    return await db.guests.get(id);
  },
  
  async create(guestData) {
    return await db.guests.add({
      ...guestData,
      createdAt: new Date().toISOString()
    });
  },
  
  async update(id, guestData) {
    await db.guests.update(id, guestData);
    return await db.guests.get(id);
  },
  
  async delete(id) {
    return await db.guests.delete(id);
  },

  async updateRsvpStatus(id, status) {
    await db.guests.update(id, { rsvpStatus: status });
    return await db.guests.get(id);
  },

  async getByEmail(email) {
    console.log("Getting guests with email:", email);
    return await db.guests.where('email').equals(email).toArray();
  },

  async getByEmailAndEvent(email, eventId) {
    console.log(`Getting guests with email: ${email} for event: ${eventId}`);
    return await db.guests
      .where('email')
      .equals(email)
      .and(guest => guest.eventId === eventId)
      .toArray();
  }
};

// Template functions
export const templateService = {
  async getAll(userId) {
    console.log("==== TEMPLATE SERVICE GET ALL - FULL DEBUG =====");
    console.log("Getting templates for user:", userId);
    
    try {
      // First get all templates to check if DB has any
      const allTemplatesInDb = await db.templates.toArray();
      console.log(`Total templates in database: ${allTemplatesInDb.length}`);
      
      // Get user-specific templates
      const userTemplates = await db.templates.where('userId').equals(userId).toArray();
      console.log(`Templates for user ${userId}: ${userTemplates.length}`);
      
      // Examine each template in detail
      userTemplates.forEach((template, idx) => {
        console.log(`TEMPLATE ${idx}:`);
        console.log(`- ID: ${template.id}`);
        console.log(`- Name: ${template.name}`);
        console.log(`- Elements property exists: ${!!template.elements}`);
        console.log(`- Elements count: ${template.elements?.length || 0}`);
        
        if (template.elements && template.elements.length > 0) {
          const firstElement = template.elements[0];
          console.log(`- First element type: ${firstElement.type}`);
          console.log(`- First element props: ${JSON.stringify(firstElement).substring(0, 100)}...`);
        }
      });
      
      if (userTemplates.length === 0) {
        console.log("No templates found for this user.");
      }
      
      return userTemplates;
    } catch (err) {
      console.error("ERROR in templateService.getAll:", err);
      return [];
    } finally {
      console.log("==== END TEMPLATE SERVICE GET ALL =====");
    }
  },
  
  async getById(id) {
    console.log("==== TEMPLATE SERVICE - GET BY ID =====");
    console.log("Getting template with ID:", id, "Type:", typeof id);
    
    try {
      // Try to get the template from the database
      let template;
      
      // If ID is a string that can be parsed as a number, try both
      if (typeof id === 'string') {
        // Try direct lookup first
        template = await db.templates.get(id);
        
        // If not found and it's a number string, try as number
        if (!template && !isNaN(parseInt(id, 10))) {
          const numericId = parseInt(id, 10);
          console.log("Trying numeric ID:", numericId);
          template = await db.templates.get(numericId);
        }
      } else {
        // If it's already a number or other type, try direct lookup
        template = await db.templates.get(id);
      }
      
      // Log what we found
      if (template) {
        console.log("Found template:", template.name);
        console.log("Template has elements:", !!template.elements);
        console.log("Elements count:", template.elements?.length || 0);
        
        if (template.elements?.length > 0) {
          console.log("First element type:", template.elements[0].type);
        }
      } else {
        console.log("No template found with ID:", id);
      }
      
      return template;
    } catch (err) {
      console.error("Error in templateService.getById:", err);
      return null;
    } finally {
      console.log("==== END TEMPLATE SERVICE - GET BY ID =====");
    }
  },
  
  async create(templateData) {
    console.log("==== CREATE TEMPLATE WITH FULL ELEMENTS =====");
    try {
      // Validate input data
      if (!templateData) {
        console.error("templateData is undefined");
        throw new Error("Cannot save template: No data provided");
      }
      
      // Safely clone the data
      let dataToSave;
      try {
        // Try deep cloning first
        dataToSave = JSON.parse(JSON.stringify(templateData));
      } catch (err) {
        console.error("Error in deep clone, falling back to shallow copy:", err);
        // Fallback to shallow copy
        dataToSave = { ...templateData };
      }
      
      // Ensure elements is always a valid array
      if (!dataToSave.elements) {
        console.log("No elements property found, initializing as empty array");
        dataToSave.elements = [];
      }
      
      // Validate elements is actually an array
      if (!Array.isArray(dataToSave.elements)) {
        console.warn("Elements is not an array, converting to array");
        dataToSave.elements = [];
      }
      
      // Check required properties
      if (!dataToSave.userId) {
        console.error("Missing userId in template data");
        throw new Error("Template must have a userId");
      }
      
      if (!dataToSave.name) {
        console.log("No template name provided, using default");
        dataToSave.name = `Template ${new Date().toLocaleString()}`;
      }
      
      // NEW: Check if a template with the same name already exists for this user
      console.log("Checking for existing templates with the same name");
      const existingTemplates = await db.templates
        .where('userId')
        .equals(dataToSave.userId)
        .filter(template => template.name === dataToSave.name)
        .toArray();
      
      // If an existing template with the same name is found, update it instead
      if (existingTemplates && existingTemplates.length > 0) {
        const existingTemplate = existingTemplates[0];
        console.log("Found existing template with the same name. Updating instead of creating new:", existingTemplate.id);
        
        // Update the template
        dataToSave.id = existingTemplate.id;
        dataToSave.createdAt = existingTemplate.createdAt; // Preserve original creation date
        dataToSave.updatedAt = new Date().toISOString(); // Update the modification date
        
        await db.templates.update(existingTemplate.id, dataToSave);
        console.log("TEMPLATE UPDATED WITH ID:", existingTemplate.id);
        
        // Return the updated template
        return {
          ...dataToSave,
          id: existingTemplate.id
        };
      }
      
      // Remove ID if undefined (let Dexie create one)
      if (dataToSave.id === undefined) {
        delete dataToSave.id;
      }
      
      // Add timestamps
      dataToSave.createdAt = new Date().toISOString();
      dataToSave.updatedAt = new Date().toISOString();
      
      console.log("SAVING TEMPLATE DATA:", {
        name: dataToSave.name, 
        userId: dataToSave.userId,
        elementsCount: dataToSave.elements.length
      });
      
      // Add to database
      const newId = await db.templates.add(dataToSave);
      console.log("TEMPLATE SAVED WITH ID:", newId);
      
      // Get the saved template
      const savedTemplate = await db.templates.get(newId);
      console.log("SAVED TEMPLATE RETRIEVED:", !!savedTemplate);
      
      return savedTemplate;
    } catch (err) {
      console.error("ERROR SAVING TEMPLATE:", err);
      console.error(err.stack);
      throw err;
    }
  },
  
  async update(id, templateData) {
    console.log("==== TEMPLATE UPDATE DEEP DEBUG =====");
    
    try {
      // First, get the existing template to compare
      const existingTemplate = await db.templates.get(id);
      console.log("EXISTING TEMPLATE:", existingTemplate ? "FOUND" : "NOT FOUND");
      if (existingTemplate) {
        console.log("Existing elements count:", existingTemplate.elements?.length || 0);
      }
      
      // Log incoming data
      console.log("UPDATE DATA:", templateData);
      console.log("NEW ELEMENTS COUNT:", templateData.elements?.length || 0);
      
      // Create safe update data (remove id from the update object)
      const { id: _, ...dataToUpdate } = templateData;
      
      // Force elements to be included and properly formed
      if (!Array.isArray(dataToUpdate.elements)) {
        console.warn("Elements is not an array in update data, using empty array");
        dataToUpdate.elements = [];
      }
      
      // Create direct string representation for comparison
      if (dataToUpdate.elements.length > 0 && existingTemplate?.elements?.length > 0) {
        console.log("COMPARING FIRST ELEMENT:");
        console.log("OLD:", JSON.stringify(existingTemplate.elements[0]).substring(0, 100));
        console.log("NEW:", JSON.stringify(dataToUpdate.elements[0]).substring(0, 100));
      }
      
      // Add updated timestamp
      dataToUpdate.updatedAt = new Date().toISOString();
      
      // Force a complete replace of the template by using put instead of update
      // This ensures all properties including elements are completely replaced
      console.log("Using PUT to completely replace template:", id);
      
      // Combine the ID with the update data for a complete replace
      const completeData = { id, ...dataToUpdate };
      await db.templates.put(completeData);
      
      // Retrieve the updated template to verify the update
      const updatedTemplate = await db.templates.get(id);
      
      if (updatedTemplate) {
        console.log("Update successful!");
        console.log("Updated elements count:", updatedTemplate.elements?.length || 0);
        
        if (updatedTemplate.elements.length > 0) {
          console.log("First element after update:", 
            JSON.stringify(updatedTemplate.elements[0]).substring(0, 100));
        }
      } else {
        console.error("Failed to retrieve updated template!");
      }
      
      return updatedTemplate;
    } catch (err) {
      console.error("Error in templateService.update:", err);
      throw err;
    } finally {
      console.log("==== END TEMPLATE UPDATE DEBUG =====");
    }
  },
  
  async delete(id) {
    return await db.templates.delete(id);
  },

  async getAllTemplates() {
    try {
      // Get all templates from the database with no defaults
      const dbTemplates = await db.templates.toArray();
      console.log("Retrieved actual templates from database:", dbTemplates);
      
      // Return only what's in the database, no default templates
      return dbTemplates || [];
    } catch (err) {
      console.error("Error getting templates:", err);
      return [];
    }
  }
}; 